/**
 * Product page runtime.
 *
 * ── Contract between blocks ────────────────────────────────────────────────
 * No block reads another block's markup. They communicate through:
 *
 *   state    Theme.product.get(sectionId) -> { variants, variant, quantity }
 *   events   variant:change / quantity:change / product:updated
 *            (pubsub subscribers AND real CustomEvents on document)
 *   hooks    documented data attributes this file updates in place:
 *              [data-variant-price]      price markup target
 *              [data-variant-sku]        SKU text target
 *              [data-variant-inventory]  stock message target
 *              [data-add-to-cart]        any submit button for the product form
 *
 * A block opts into an update by carrying the attribute. It never needs to
 * know which other blocks exist, or in what order the merchant placed them.
 *
 * Everything is bound through delegation on `document`, so re-rendering a
 * block in the theme editor never leaves a dead listener behind.
 */
(function () {
  'use strict';

  const { cart, pubsub, utils, strings } = window.Theme;

  /* ------------------------------------------------------------------ */
  /* Central state                                                       */
  /* ------------------------------------------------------------------ */

  const states = new Map();

  const productState = {
    get(sectionId) {
      return states.get(sectionId);
    },

    register(sectionId, data) {
      states.set(sectionId, data);
      return data;
    },

    setVariant(sectionId, variant) {
      const state = states.get(sectionId);
      if (!state) return;
      state.variant = variant;

      // Keep the element's own attribute in step with the state it seeds.
      //
      // <product-page> reads data-selected-variant-id once, in
      // connectedCallback, to build the initial state. If the shopper then
      // changes variant and the element is ever reconnected without a fresh
      // server render, it would seed from the stale attribute and silently
      // revert their choice while the form's id input still said otherwise.
      // Writing it here also makes the current variant readable from the DOM,
      // which is what an app or a debugging session would reach for.
      const host = document.querySelector(`product-page[data-section-id="${sectionId}"]`);
      if (host && variant) host.dataset.selectedVariantId = String(variant.id);

      pubsub.publish(pubsub.EVENTS.variantChange, { sectionId, variant, state });
      pubsub.publish(pubsub.EVENTS.productUpdate, { sectionId, state });
    },

    setQuantity(sectionId, quantity) {
      const state = states.get(sectionId);
      if (!state || state.quantity === quantity) return;
      state.quantity = quantity;
      pubsub.publish(pubsub.EVENTS.quantityChange, { sectionId, quantity, state });
      pubsub.publish(pubsub.EVENTS.productUpdate, { sectionId, state });
    },
  };

  window.Theme.product = productState;

  /* ------------------------------------------------------------------ */
  /* <product-page> — owns the state for one product section             */
  /* ------------------------------------------------------------------ */

  class ProductPage extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;

      let variants = [];
      const node = this.querySelector('[data-variant-data]');
      if (node) {
        try {
          variants = JSON.parse(node.textContent);
        } catch {
          variants = [];
        }
      }

      const selectedId = Number(this.dataset.selectedVariantId);
      const state = productState.register(this.sectionId, {
        sectionId: this.sectionId,
        productId: Number(this.dataset.productId),
        url: this.dataset.url,
        updateUrl: this.dataset.updateUrl === 'true',
        variants,
        variant: variants.find((v) => v.id === selectedId) || variants[0] || null,
        quantity: 1,
      });

      // Blocks rendered after this element connects (theme editor) read the
      // state directly; blocks already present get the initial broadcast.
      pubsub.publish(pubsub.EVENTS.productUpdate, { sectionId: this.sectionId, state });
    }

    disconnectedCallback() {
      states.delete(this.sectionId);
    }
  }

  if (!customElements.get('product-page')) customElements.define('product-page', ProductPage);

  /* ------------------------------------------------------------------ */
  /* Variant picker                                                      */
  /* ------------------------------------------------------------------ */

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.page = this.closest('product-page');
      this.sectionId = this.page?.dataset.sectionId;
      this.hideUnavailable = this.dataset.hideUnavailable === 'true';

      this.addEventListener('change', () => this.onChange());

      // Another block (sticky bar, quantity break, upsell) can ask for a
      // variant without touching these inputs.
      this.requestHandler = (event) => {
        if (event.detail.sectionId !== this.sectionId) return;
        this.selectVariantById(event.detail.variantId);
      };
      document.addEventListener('variant:request', this.requestHandler);

      this.updateAvailability();
    }

    disconnectedCallback() {
      document.removeEventListener('variant:request', this.requestHandler);
    }

    get state() {
      return productState.get(this.sectionId);
    }

    /** Selected option values, in option order. */
    get selectedOptions() {
      return Array.from(this.querySelectorAll('fieldset')).map((fieldset) => {
        const select = fieldset.querySelector('select[data-variant-option]');
        if (select) return select.value;
        return fieldset.querySelector('input[data-variant-option]:checked')?.value ?? null;
      });
    }

    findVariant(options) {
      const variants = this.state?.variants || [];
      return variants.find((v) => v.options.every((option, i) => option === options[i]));
    }

    onChange() {
      const variant = this.findVariant(this.selectedOptions);

      this.updateSelectedLabels();
      this.updateAvailability();
      this.writeVariantId(variant);
      this.updateUrl(variant);

      productState.setVariant(this.sectionId, variant || null);
    }

    /** Drives the inputs from a variant id, then runs the normal change path. */
    selectVariantById(variantId) {
      const variant = (this.state?.variants || []).find((v) => v.id === Number(variantId));
      if (!variant) return;

      this.querySelectorAll('fieldset').forEach((fieldset, index) => {
        const value = variant.options[index];
        const select = fieldset.querySelector('select[data-variant-option]');
        if (select) {
          select.value = value;
          return;
        }
        const input = fieldset.querySelector(`input[data-variant-option][value="${CSS.escape(value)}"]`);
        if (input) input.checked = true;
      });

      this.onChange();
    }

    updateSelectedLabels() {
      this.querySelectorAll('fieldset').forEach((fieldset) => {
        const target = fieldset.querySelector('[data-option-selected]');
        if (!target) return;
        const select = fieldset.querySelector('select[data-variant-option]');
        target.textContent = select
          ? select.value
          : (fieldset.querySelector('input[data-variant-option]:checked')?.value ?? '');
      });
    }

    /**
     * Marks option values that cannot be combined with the current selection.
     * For each option group the other groups stay fixed, so the visitor can
     * always switch within a group and see what that would make unavailable.
     */
    updateAvailability() {
      const variants = this.state?.variants || [];
      if (!variants.length) return;

      const selected = this.selectedOptions;

      this.querySelectorAll('fieldset').forEach((fieldset, optionIndex) => {
        const controls = fieldset.querySelectorAll('input[data-variant-option], option');

        controls.forEach((control) => {
          const candidate = [...selected];
          candidate[optionIndex] = control.value;

          const match = variants.find((v) =>
            v.options.every((option, i) => candidate[i] == null || option === candidate[i])
          );
          const available = Boolean(match && match.available);

          if (control.tagName === 'OPTION') {
            control.toggleAttribute('data-unavailable', !available);
            control.disabled = this.hideUnavailable && !available;
          } else {
            const label = fieldset.querySelector(`label[for="${CSS.escape(control.id)}"]`);
            label?.toggleAttribute('data-unavailable', !available);
            if (this.hideUnavailable) {
              control.hidden = !available;
              if (label) label.hidden = !available;
            }
          }
        });
      });
    }

    writeVariantId(variant) {
      // The id input lives in this block, but any form-associated input with
      // the hook is updated so a section can render more than one.
      const scope = this.page || document;
      scope.querySelectorAll('[data-variant-id]').forEach((input) => {
        input.value = variant ? variant.id : '';
      });
    }

    updateUrl(variant) {
      if (!variant || this.state?.updateUrl !== true) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
  }

  if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);

  /* ------------------------------------------------------------------ */
  /* Gallery                                                             */
  /* ------------------------------------------------------------------ */

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('[data-gallery-stage]');
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
      this.items = Array.from(this.querySelectorAll('[data-gallery-item]'));

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.show(thumb.dataset.mediaId));
      });

      // Keep thumbnails in step when the visitor swipes the mobile row.
      if (this.stage && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
                this.markActive(entry.target.dataset.mediaId);
              }
            });
          },
          { root: this.stage, threshold: [0.6] }
        );
        this.items.forEach((item) => this.observer.observe(item));
      }

      this.unsubscribe = pubsub.subscribe(pubsub.EVENTS.variantChange, ({ variant }) => {
        if (variant?.featured_media?.id) this.show(String(variant.featured_media.id));
      });
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.unsubscribe?.();
    }

    show(mediaId) {
      const item = this.items.find((el) => el.dataset.mediaId === mediaId);
      if (!item) return;

      this.items.forEach((el) => el.toggleAttribute('data-inactive', el !== item));
      item.scrollIntoView({ behavior: utils.scrollBehavior(), block: 'nearest', inline: 'start' });
      this.markActive(mediaId);
    }

    markActive(mediaId) {
      this.thumbs.forEach((thumb) => {
        thumb.setAttribute('aria-current', String(thumb.dataset.mediaId === mediaId));
      });
    }
  }

  if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);

  /* ------------------------------------------------------------------ */
  /* Quantity steppers (delegated)                                       */
  /* ------------------------------------------------------------------ */

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-quantity-change]');
    if (!button) return;

    // The cart drawer runs its own stepper so it can sync with Shopify.
    if (button.closest('cart-drawer')) return;

    const input = button.closest('[data-quantity]')?.querySelector('[data-quantity-input]');
    if (!input) return;

    event.preventDefault();

    const step = parseInt(input.step, 10) || 1;
    const min = parseInt(input.min, 10) || 0;
    const max = input.max ? parseInt(input.max, 10) : Infinity;
    const current = parseInt(input.value, 10) || min;
    const next = Math.min(max, Math.max(min, current + parseInt(button.dataset.quantityChange, 10) * step));

    if (next === current) return;
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-quantity-input]');
    if (!input || input.closest('cart-drawer')) return;

    const page = input.closest('product-page');
    if (!page) return;

    productState.setQuantity(page.dataset.sectionId, parseInt(input.value, 10) || 1);
  });

  /* ------------------------------------------------------------------ */
  /* Add to cart (delegated — survives theme editor re-renders)          */
  /* ------------------------------------------------------------------ */

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-product-form]');
    if (!form) return;

    // Dynamic checkout buttons submit natively and must not be intercepted.
    const submitter = event.submitter;
    if (submitter && submitter.name !== 'add') return;

    event.preventDefault();

    // Every button that submits this form, wherever it lives (inline, sticky
    // bar, a CRO block) — they all share one form, so one loading state.
    //
    // `form.id` is NOT usable here: a form's named-item lookup shadows the
    // property, and this form owns an <input name="id"> for the variant, so
    // `form.id` returns that element. Read the attribute instead.
    const page = form.closest('product-page');
    const formId = form.getAttribute('id');
    const buttons = Array.from(
      (page || document).querySelectorAll(
        `[data-add-to-cart][form="${CSS.escape(formId)}"], form[id="${CSS.escape(formId)}"] [data-add-to-cart]`
      )
    );
    const errorTargets = Array.from((page || document).querySelectorAll('[data-product-form-error]'));

    if (buttons.some((b) => b.getAttribute('aria-disabled') === 'true')) return;

    setError(errorTargets, '');
    setLoading(buttons, true);

    try {
      const formData = new FormData(form);
      const id = Number(formData.get('id'));
      if (!id) throw new Error(strings.unavailable);

      await cart.add({
        id,
        quantity: Number(formData.get('quantity') || 1),
        properties: collectProperties(formData),
      });
    } catch (error) {
      setError(errorTargets, error.data?.description || error.message || strings.cartError);
    } finally {
      setLoading(buttons, false);
    }
  });

  function collectProperties(formData) {
    const properties = {};
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && value !== '') properties[match[1]] = value;
    }
    return Object.keys(properties).length ? properties : undefined;
  }

  function setLoading(buttons, isLoading) {
    buttons.forEach((button) => {
      button.classList.toggle('button--loading', isLoading);
      button.setAttribute('aria-disabled', String(isLoading));
    });
  }

  function setError(targets, message) {
    targets.forEach((target) => {
      target.textContent = message;
      target.hidden = !message;
    });
  }

  /* ------------------------------------------------------------------ */
  /* Hook updaters — the only place that writes variant-dependent markup */
  /* ------------------------------------------------------------------ */

  function priceMarkup(variant) {
    const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
    const label = onSale ? strings.salePrice : strings.regularPrice;

    let html = `<span class="visually-hidden">${label}</span>`;
    html += `<span class="${onSale ? 'price__sale' : 'price__regular'}">${utils.formatMoney(variant.price)}</span>`;

    if (onSale) {
      html += `<span class="visually-hidden">${strings.regularPrice}</span>`;
      html += `<s class="price__compare">${utils.formatMoney(variant.compare_at_price)}</s>`;
    }

    if (variant.unit_price_measurement) {
      const m = variant.unit_price_measurement;
      const ref = m.reference_value !== 1 ? m.reference_value : '';
      html += `<span class="price__unit"><span class="visually-hidden">${strings.unitPrice}</span>${utils.formatMoney(
        variant.unit_price
      )} / ${ref}${m.reference_unit}</span>`;
    }

    return html;
  }

  function savingsMarkup(variant) {
    if (!variant.compare_at_price || variant.compare_at_price <= variant.price) return '';
    const saved = variant.compare_at_price - variant.price;
    const percent = Math.round((saved * 100) / variant.compare_at_price);
    return { percent, amount: utils.formatMoney(saved) };
  }

  pubsub.subscribe(pubsub.EVENTS.variantChange, ({ sectionId, variant }) => {
    const scope = document.querySelector(`product-page[data-section-id="${sectionId}"]`);
    if (!scope) return;

    /* Buy buttons ---------------------------------------------------- */
    scope.querySelectorAll('[data-add-to-cart]').forEach((button) => {
      const label = button.querySelector('[data-add-to-cart-text]');
      const defaultLabel = button.dataset.defaultLabel || strings.addToCart;

      if (!variant) {
        button.disabled = true;
        if (label) label.textContent = strings.unavailable;
      } else if (!variant.available) {
        button.disabled = true;
        if (label) label.textContent = strings.soldOut;
      } else {
        button.disabled = false;
        if (label) label.textContent = defaultLabel;
      }
    });

    if (!variant) return;

    /* Price ---------------------------------------------------------- */
    scope.querySelectorAll('[data-variant-price]').forEach((wrapper) => {
      const priceEl = wrapper.querySelector('.price');
      if (priceEl) {
        priceEl.classList.toggle(
          'price--on-sale',
          Boolean(variant.compare_at_price && variant.compare_at_price > variant.price)
        );
        priceEl.innerHTML = priceMarkup(variant);
      }

      const badge = wrapper.querySelector('[data-variant-savings]');
      if (badge) {
        const saved = savingsMarkup(variant);
        if (!saved) {
          badge.hidden = true;
        } else {
          badge.hidden = false;
          badge.textContent =
            badge.dataset.format === 'amount'
              ? strings.saveAmount.replace('[amount]', saved.amount)
              : strings.savePercent.replace('[percent]', saved.percent);
        }
      }
    });

    /* SKU ------------------------------------------------------------ */
    scope.querySelectorAll('[data-variant-sku]').forEach((el) => {
      el.textContent = variant.sku || '';
      el.closest('[data-variant-sku-wrapper]')?.toggleAttribute('hidden', !variant.sku);
    });

    /* Inventory ------------------------------------------------------ */
    scope.querySelectorAll('[data-variant-inventory]').forEach((el) => {
      const threshold = parseInt(el.dataset.lowStockThreshold, 10) || 0;
      const qty = variant.inventory_quantity;
      const tracked = variant.inventory_management !== null;

      if (!tracked || !variant.available || qty === undefined) {
        el.hidden = !variant.available ? false : true;
        if (!variant.available) {
          el.textContent = strings.outOfStock;
          el.className = 'product-inventory product-inventory--out';
        }
        return;
      }

      el.hidden = false;
      if (qty <= threshold) {
        el.className = 'product-inventory product-inventory--low';
        el.textContent = strings.lowStock.replace('[count]', qty);
      } else {
        el.className = 'product-inventory product-inventory--in';
        el.textContent = strings.inStock;
      }
    });

    /* Quantity rules ------------------------------------------------- */
    scope.querySelectorAll('[data-quantity-input]').forEach((input) => {
      if (variant.quantity_rule) {
        input.min = variant.quantity_rule.min ?? 1;
        input.step = variant.quantity_rule.increment ?? 1;
        if (variant.quantity_rule.max != null) input.max = variant.quantity_rule.max;
        else input.removeAttribute('max');
      }
    });
  });

  /* ------------------------------------------------------------------ */
  /* Remember each button's configured label (delegated, re-runnable)    */
  /* ------------------------------------------------------------------ */

  function captureDefaultLabels(scope = document) {
    scope.querySelectorAll('[data-add-to-cart]:not([data-default-label])').forEach((button) => {
      const label = button.querySelector('[data-add-to-cart-text]');
      if (label) button.dataset.defaultLabel = label.textContent.trim();
    });
  }

  captureDefaultLabels();
  document.addEventListener('shopify:section:load', (e) => captureDefaultLabels(e.target));
  document.addEventListener('shopify:block:select', (e) => captureDefaultLabels(e.target));
})();
